export const LEGACY_COMPTour_HOSTS = new Set([
  'skullkingappgrade.fr',
  'www.skullkingappgrade.fr',
]);

export const COMPTOUR_ORIGIN = 'https://comptour.fr';
export const DOMAIN_MIGRATION_ENDPOINT = `${COMPTOUR_ORIGIN}/api/migrate-local-data`;
export const DOMAIN_MIGRATION_VERSION = 1;
export const DOMAIN_MIGRATION_COMPRESSION_DEADLINE_MS = 2_500;

const APP_STORAGE_PREFIXES = ['skullking_', 'skull-arena:'] as const;
const APP_STORAGE_KEYS = new Set([
  'table-de-jeux:last-opened-game:v1',
]);

export interface DomainMigrationPayload {
  version: 1;
  sourceOrigin: string;
  destinationPath: string;
  createdAt: string;
  entries: Record<string, string>;
}

export interface EncodedMigrationPayload {
  encoding: 'plain' | 'gzip-base64';
  payload: string;
}

export function encodePlainDomainMigrationPayload(
  transfer: DomainMigrationPayload,
): EncodedMigrationPayload {
  const json = JSON.stringify(transfer);
  // Vercel Functions accept at most 4.5 MB. Keeping a margin also accounts for
  // URL-encoding overhead in the HTML form fallback.
  if (new Blob([json]).size > 3_000_000) {
    throw new Error('migration-payload-too-large');
  }
  return { encoding: 'plain', payload: json };
}

type CompressionStreamConstructor = new (format: 'gzip') => {
  readable: ReadableStream<Uint8Array>;
  writable: WritableStream<Uint8Array>;
};

export function isLegacyComptourHostname(hostname: string): boolean {
  return LEGACY_COMPTour_HOSTS.has(hostname.toLocaleLowerCase('en'));
}

export function isComptourStorageKey(key: string): boolean {
  return APP_STORAGE_KEYS.has(key) || APP_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix));
}

export function collectComptourStorage(storage: Storage): Record<string, string> {
  const entries: Record<string, string> = {};
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (!key || !isComptourStorageKey(key)) continue;
    const value = storage.getItem(key);
    if (value !== null) entries[key] = value;
  }
  return entries;
}

export function safeDestinationPath(location: Pick<Location, 'pathname' | 'search' | 'hash'>): string {
  const candidate = `${location.pathname || '/'}${location.search || ''}${location.hash || ''}`;
  return candidate.startsWith('/') && !candidate.startsWith('//') ? candidate : '/';
}

export function createDomainMigrationPayload(
  storage: Storage,
  location: Pick<Location, 'origin' | 'pathname' | 'search' | 'hash'>,
): DomainMigrationPayload {
  return {
    version: DOMAIN_MIGRATION_VERSION,
    sourceOrigin: location.origin,
    destinationPath: safeDestinationPath(location),
    createdAt: new Date().toISOString(),
    entries: collectComptourStorage(storage),
  };
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunkSize = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    const slice = bytes.subarray(offset, offset + chunkSize);
    const charCodes = new Array<number>(slice.length);
    for (let index = 0; index < slice.length; index += 1) charCodes[index] = slice[index];
    binary += String.fromCharCode.apply(null, charCodes);
  }
  return window.btoa(binary);
}

export async function encodeDomainMigrationPayload(
  transfer: DomainMigrationPayload,
): Promise<EncodedMigrationPayload> {
  const json = JSON.stringify(transfer);
  const CompressionStreamApi = (window as unknown as { CompressionStream?: CompressionStreamConstructor }).CompressionStream;
  if (typeof CompressionStreamApi === 'function') {
    const stream = new CompressionStreamApi('gzip');
    // Start reading before writing. Safari applies backpressure to the writable
    // side and can otherwise leave writer.write() pending forever.
    const compressedBytes = new Response(stream.readable).arrayBuffer();
    const writer = stream.writable.getWriter();
    await writer.write(new TextEncoder().encode(json));
    await writer.close();
    const compressed = new Uint8Array(await compressedBytes);
    return { encoding: 'gzip-base64', payload: bytesToBase64(compressed) };
  }

  return encodePlainDomainMigrationPayload(transfer);
}

export async function encodeDomainMigrationPayloadWithFallback(
  transfer: DomainMigrationPayload,
  deadlineMs = DOMAIN_MIGRATION_COMPRESSION_DEADLINE_MS,
): Promise<EncodedMigrationPayload> {
  let deadline: number | undefined;
  const fallback = new Promise<EncodedMigrationPayload>((resolve, reject) => {
    deadline = window.setTimeout(
      () => {
        try {
          resolve(encodePlainDomainMigrationPayload(transfer));
        } catch (error) {
          reject(error);
        }
      },
      deadlineMs,
    );
  });

  try {
    const compressedOrPlain = encodeDomainMigrationPayload(transfer)
      .catch(() => encodePlainDomainMigrationPayload(transfer));
    return await Promise.race([compressedOrPlain, fallback]);
  } finally {
    if (deadline !== undefined) window.clearTimeout(deadline);
  }
}

export function submitDomainMigration(encoded: EncodedMigrationPayload): void {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = DOMAIN_MIGRATION_ENDPOINT;
  form.target = '_self';
  form.acceptCharset = 'UTF-8';
  form.style.display = 'none';

  Object.entries(encoded).forEach(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
}
